import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const { TextArea } = Input;

const Proveedores = () => {
  const { hasPermission } = usePermissions();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadProveedores = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/gestion/proveedores');
      if (response.ok) {
        const data = await response.json();
        setProveedores(data);
        setPagination(prev => ({ ...prev, total: data.length }));
      } else {
        message.error('Error al cargar los proveedores');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los proveedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProveedores();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const proveedoresFiltrados = proveedores.filter(prov =>
    !filtroNombre || prov.pro_nombre?.toLowerCase().includes(filtroNombre.toLowerCase())
  );

  const proveedoresPaginados = proveedoresFiltrados.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const handleOpenModal = (proveedor = null) => {
    setEditingProveedor(proveedor);
    if (proveedor) {
      form.setFieldsValue({
        nombre: proveedor.pro_nombre,
        telefono: proveedor.pro_telefono,
        email: proveedor.pro_email,
        direccion: proveedor.pro_direccion,
        activo: proveedor.pro_activo,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ activo: true });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingProveedor) {
        const response = await apiClient.put(
          `/gestion/proveedores/${editingProveedor.pro_id}`,
          values
        );
        if (response.ok) {
          message.success('Proveedor actualizado correctamente');
          setModalVisible(false);
          loadProveedores();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el proveedor');
        }
      } else {
        const response = await apiClient.post('/gestion/proveedores', values);
        if (response.ok) {
          message.success('Proveedor creado correctamente');
          setModalVisible(false);
          loadProveedores();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el proveedor');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar el proveedor');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/proveedores/${id}`);
      if (response.ok) {
        message.success('Proveedor eliminado correctamente');
        loadProveedores();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el proveedor');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el proveedor');
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'pro_nombre',
      key: 'pro_nombre',
    },
    {
      title: 'Teléfono',
      dataIndex: 'pro_telefono',
      key: 'pro_telefono',
      render: (tel) => tel || '-',
    },
    {
      title: 'Email',
      dataIndex: 'pro_email',
      key: 'pro_email',
      render: (email) => email || '-',
    },
    {
      title: 'Dirección',
      dataIndex: 'pro_direccion',
      key: 'pro_direccion',
      render: (dir) => dir || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'pro_activo',
      key: 'pro_activo',
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    ...((hasPermission('proveedores', 'editar') || hasPermission('proveedores', 'eliminar')) ? [{
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          {hasPermission('proveedores', 'editar') && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            >
              Editar
            </Button>
          )}
          {hasPermission('proveedores', 'eliminar') && (
            <Popconfirm
              title="¿Estás seguro de eliminar este proveedor?"
              onConfirm={() => handleDelete(record.pro_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Gestión de Proveedores
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadProveedores}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('proveedores', 'crear') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenModal(null)}
              >
                Nuevo Proveedor
              </Button>
            )}
          </Space>

          <div style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#2d2d2d',
            borderRadius: 8,
          }}>
            <Input
              placeholder="Buscar proveedor por nombre..."
              value={filtroNombre}
              onChange={(e) => {
                setFiltroNombre(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
              style={{ maxWidth: 400 }}
            />
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {proveedoresPaginados.map(proveedor => (
              <Card
                key={proveedor.pro_id}
                hoverable
                style={{ borderLeft: '4px solid #1890ff', borderRadius: '8px' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                      {proveedor.pro_nombre}
                    </div>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Teléfono: {proveedor.pro_telefono || '-'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Email: {proveedor.pro_email || '-'}
                    </div>
                  </div>
                  {(hasPermission('proveedores', 'editar') || hasPermission('proveedores', 'eliminar')) && (
                    <Space wrap>
                      {hasPermission('proveedores', 'editar') && (
                        <Button
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => handleOpenModal(proveedor)}
                        >
                          Editar
                        </Button>
                      )}
                      {hasPermission('proveedores', 'eliminar') && (
                        <Popconfirm
                          title="¿Estás seguro de eliminar este proveedor?"
                          onConfirm={() => handleDelete(proveedor.pro_id)}
                          okText="Sí"
                          cancelText="No"
                        >
                          <Button type="link" danger icon={<DeleteOutlined />}>
                            Eliminar
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Card>
            ))}
            {proveedoresFiltrados.length > 0 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={proveedoresFiltrados.length}
                  onChange={(page, pageSize) => {
                    setPagination(prev => ({ ...prev, current: page, pageSize }));
                  }}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total) => `Total: ${total} proveedores`}
                />
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={proveedoresPaginados}
            loading={loading}
            rowKey="pro_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: proveedoresFiltrados.length,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} proveedores`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
              },
            }}
          />
        )}

        <Modal
          title={editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={600}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[{ required: true, message: 'El nombre es requerido' }]}
            >
              <Input placeholder="Nombre del proveedor" />
            </Form.Item>

            <Form.Item name="telefono" label="Teléfono">
              <Input placeholder="Teléfono" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Email inválido' }]}
            >
              <Input placeholder="email@ejemplo.com" />
            </Form.Item>

            <Form.Item name="direccion" label="Dirección">
              <TextArea rows={2} placeholder="Dirección" />
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Proveedores;
